import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FaqAccordion from '@/components/feature/FaqAccordion';
import { FAQS } from '@/mocks/faqs';
import { SITE } from '@/mocks/site';

const EXTRA = [
  {
    q: '档案里会出现具体的人名或网名吗？',
    a: '只有在公开讨论本身构成文化现象时，才会以化名或模糊方式提及，并且不会附加任何联系方式、住址或其他私人标识。涉及个人生活的部分一律不收，这是档案室最硬的一条边界。',
  },
  {
    q: '为什么有的条目正文看起来不够「完整」？',
    a: '因为原始材料本身就残缺。我们更愿意如实呈现缺口，也不愿用推测填满它。条目开头的「材料完整度」一栏，就是为这件事准备的。',
  },
];

export default function Faq() {
  const allFaqs = useMemo(() => [...FAQS, ...EXTRA], []);

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: allFaqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '常见问题', item: '/faq' },
        ],
      },
    ],
    [allFaqs],
  );

  return (
    <>
      <Seo
        title="常见问题 | 黑料百科 · 材料来源与隐私边界说明"
        description="关于黑料百科的收录范围、材料来源、隐私边界、线索投递方式与档案修订机制的常见问题解答，帮你快速了解这座档案馆怎么运作。"
        keywords="黑料百科常见问题,材料来源,隐私边界,线索投递,档案修订"
        path="/faq"
        ogType="website"
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="w-full pt-[68px] md:pt-[74px]">
        <section className="w-full border-b border-background-200 bg-background-100 archive-grain">
          <div className="w-full px-4 md:px-8 py-10 md:py-16">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '常见问题' }]} />
            <h1 className="mt-5 max-w-3xl font-heading text-3xl md:text-5xl leading-tight text-foreground-950">
              常见问题
            </h1>
            <p className="mt-5 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
              这里汇总了新读者问得最多的问题，主要围绕材料来源、隐私边界与投稿方式。
              如果下面没有你要的答案，欢迎直接写信给档案室。
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-20">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
              <div className="lg:col-span-8">
                <FaqAccordion items={allFaqs} />
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-28 flex flex-col gap-5">
                  <div className="rounded-lg border border-background-200 bg-background-100/70 p-6">
                    <span className="text-[11px] tracking-[0.25em] text-primary-500">
                      QUICK LINKS
                    </span>
                    <h2 className="mt-3 font-heading text-lg text-foreground-950">
                      也许你还想看看
                    </h2>
                    <ul className="mt-4 space-y-3 text-sm">
                      <li>
                        <Link
                          to="/archive"
                          className="flex items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-archive-2-line text-primary-500"></i>
                          考古档案库
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/about"
                          className="flex items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-information-line text-primary-500"></i>
                          关于我们与内容原则
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/about#contact"
                          className="flex items-center gap-2 text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-mail-line text-primary-500"></i>
                          联系方式
                        </Link>
                      </li>
                    </ul>
                  </div>

                  <div className="rounded-lg border border-dashed border-accent-500/40 bg-accent-500/5 p-6">
                    <div className="flex items-center gap-2 text-xs tracking-widest text-accent-500 mb-3">
                      <i className="ri-question-answer-line"></i>
                      还有疑问
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      把问题发到档案室邮箱，我们会把高频问题补进这一页。
                    </p>
                    <a
                      href={`mailto:${SITE.email}`}
                      className="mt-4 inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {SITE.email}
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}